// RapidCcsModals.js
// Modal, Toggle, Settings, Physical Exam picker, and Rapid Order List manager.
// React 16.14 compatible — class components.
//
// All server calls go through props.api (created once in RapidCcsApp and passed
// down) — nothing here imports rapidCcsApi directly.

import React from "react";
import "./rapidCcs.css";
// --------------------------------------------------------------------------
// Generic modal
// --------------------------------------------------------------------------
export class Modal extends React.Component {
  componentDidMount() {
    document.addEventListener("keydown", this.handleKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKey);
  }

  handleKey = (e) => {
    if (e.key === "Escape" && this.props.onClose) this.props.onClose();
  };

  stop = (e) => {
    e.stopPropagation();
  };

  render() {
    const props = this.props;
    return (
      <div className="rccs-modal-backdrop" onClick={props.onClose}>
        <div
          className="rccs-modal"
          role="dialog"
          aria-modal="true"
          aria-label={props.title}
          onClick={this.stop}
        >
          {!this.props.titleHidden && (
            <div className="rccs-modal__head">
              <h3>{props.title}</h3>
              <button
                className="rccs-modal__x"
                onClick={props.onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
          <div className="rccs-modal__body">{props.children}</div>
          {props.footer && (
            <div className="rccs-modal__foot">{props.footer}</div>
          )}
        </div>
      </div>
    );
  }
}

// --------------------------------------------------------------------------
// Toggle switch
// --------------------------------------------------------------------------
export class Toggle extends React.Component {
  handleChange = (e) => {
    this.props.onChange(e.target.checked);
  };

  render() {
    return (
      <label className="rccs-toggle">
        <input
          type="checkbox"
          checked={!!this.props.checked}
          onChange={this.handleChange}
          aria-label={this.props.label}
        />
        <span className="rccs-toggle__track" />
      </label>
    );
  }
}

// --------------------------------------------------------------------------
// Settings modal — AutoPhysicalExam + PreventiveCareGrading (global)
//
// Props:
//   api                 the api object from RapidCcsApp        (required)
//   settings            {AutoPhysicalExam, PreventiveCareGrading}
//   onClose             () => void
//   onSaved             (appliedSettings) => void
//
//   Optional — font scale (the old context is gone; RapidCcsApp owns this now.
//   The row only renders when all three are provided):
//   fontScale           number
//   onFontScaleChange   (scale) => void
//   fontSteps           [{key, label, scale}]
// --------------------------------------------------------------------------
export class SettingsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      auto: !!props.settings.AutoPhysicalExam,
      prev: !!props.settings.PreventiveCareGrading,
      saving: false,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  setAuto = (checked) => {
    this.setState({ auto: checked });
  };
  setPrev = (checked) => {
    this.setState({ prev: checked });
  };

  save = () => {
    this.setState({ saving: true });
    const next = Object.assign({}, this.props.settings, {
      AutoPhysicalExam: this.state.auto,
      PreventiveCareGrading: this.state.prev,
    });
    this.props.api
      .saveRapidCcsSettings(next)
      .then((res) => {
        const applied = res && res.Settings ? res.Settings : next;
        this.props.onSaved(applied);
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({ saving: false });
        alert("Could not save settings. Try again.");
      });
  };

  render() {
    const { auto, prev, saving } = this.state;
    const { fontScale, onFontScaleChange, fontSteps } = this.props;
    const showFontRow =
      typeof fontScale === "number" &&
      typeof onFontScaleChange === "function" &&
      Array.isArray(fontSteps);

    return (
      <Modal
        title="Settings"
        onClose={this.props.onClose}
        footer={
          <React.Fragment>
            <button className="rccs-btn" onClick={this.props.onClose}>
              Cancel
            </button>
            <button
              className="rccs-btn rccs-btn--primary"
              onClick={this.save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
          </React.Fragment>
        }
      >
        <div className="rccs-setting-row">
          <div className="rccs-setting-row__text">
            <strong>Auto physical exam</strong>
            <span>
              Run all exam components automatically at case start. (Not graded
              when on.)
            </span>
          </div>
          <Toggle
            checked={auto}
            onChange={this.setAuto}
            label="Auto physical exam"
          />
        </div>
        <div className="rccs-setting-row">
          <div className="rccs-setting-row__text">
            <strong>Preventive care grading</strong>
            <span>Include preventive care items when grading the case.</span>
          </div>
          <Toggle
            checked={prev}
            onChange={this.setPrev}
            label="Preventive care grading"
          />
        </div>
        {showFontRow && (
          <div className="rccs-setting-row">
            <div className="rccs-setting-row__text">
              <strong>Font size</strong>
              <span>
                Scales text across the whole Rapid CCS app. Applies immediately.
              </span>
            </div>
            <div className="rccs-fontsize">
              {fontSteps.map(function (step) {
                const on = Math.abs(fontScale - step.scale) < 0.001;
                return (
                  <button
                    key={step.key}
                    type="button"
                    className={
                      "rccs-fontsize__btn" +
                      (on ? " rccs-fontsize__btn--on" : "")
                    }
                    onClick={function () {
                      onFontScaleChange(step.scale);
                    }}
                    aria-pressed={on}
                    title={step.label}
                  >
                    <span
                      style={{ fontSize: Math.round(11 * step.scale) + "px" }}
                    >
                      A
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    );
  }
}

// --------------------------------------------------------------------------
// Physical Exam picker modal (manual mode)
// Lets the user check components to run. Already-run components are marked done.
//
// Props:
//   api      the api object from RapidCcsApp (uses api.PHYSICAL_EXAM_COMPONENTS)
//   done     array of tokens already run this round-set
//   onClose  () => void
//   onRun    (tokens[]) => void
// --------------------------------------------------------------------------
export class PhysicalExamModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { checked: new Set() };
  }

  toggle = (token) => {
    this.setState(function (prevState) {
      const next = new Set(prevState.checked);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return { checked: next };
    });
  };

  run = () => {
    const checked = this.state.checked;
    const tokens = this.props.api.PHYSICAL_EXAM_COMPONENTS.map(function (c) {
      return c.token;
    }).filter(function (t) {
      return checked.has(t);
    });
    this.props.onRun(tokens);
  };

  render() {
    const doneSet = new Set(this.props.done || []);
    const checked = this.state.checked;

    return (
      <Modal
        title="Physical examination"
        onClose={this.props.onClose}
        footer={
          <React.Fragment>
            <button className="rccs-btn" onClick={this.props.onClose}>
              Cancel
            </button>
            <button
              className="rccs-btn rccs-btn--primary"
              onClick={this.run}
              disabled={checked.size === 0}
            >
              Run selected ({checked.size})
            </button>
          </React.Fragment>
        }
      >
        <div className="rccs-exam-pick">
          {this.props.api.PHYSICAL_EXAM_COMPONENTS.map((c) => {
            const isDone = doneSet.has(c.token);
            return (
              <label key={c.token} className={isDone ? "is-done" : ""}>
                <input
                  type="checkbox"
                  checked={checked.has(c.token)}
                  onChange={() => {
                    this.toggle(c.token);
                  }}
                />
                {c.display.replace("Physical Exam: ", "")}
                {isDone && <span className="rccs-done-tag">RUN</span>}
              </label>
            );
          })}
        </div>
      </Modal>
    );
  }
}

// --------------------------------------------------------------------------
// Rapid Order List manager modal
// Max 5 lists, each max 20 orders. Add orders via autocomplete.
//
// Props:
//   api           the api object from RapidCcsApp                 (required)
//   initialLists  case-start snapshot of lists                    (optional)
//   onClose       () => void
//   onChange      (listsInApiShape) => void
// --------------------------------------------------------------------------
const MAX_LISTS = 5;
const MAX_ORDERS_PER_LIST = 20;

// Normalize any list into { Name, orders:[{ID, Name}] }, tolerating both the
// new Orders:[{Id,Name}] shape and a legacy OrderIds:[n] shape.
function normalizeList(l) {
  var orders;
  if (Array.isArray(l.Orders)) {
    orders = l.Orders.map(function (o) {
      return { ID: o.Id, Name: o.Name || "Order #" + o.Id };
    });
  } else if (Array.isArray(l.orders)) {
    // Already in the manager's internal shape — pass through.
    orders = l.orders.map(function (o) {
      return { ID: o.ID, Name: o.Name || "Order #" + o.ID };
    });
  } else if (Array.isArray(l.OrderIds)) {
    orders = l.OrderIds.map(function (id) {
      return { ID: id, Name: "Order #" + id };
    });
  } else {
    orders = [];
  }
  return { Name: l.Name, orders: orders };
}

function normalizeListsPayload(data) {
  const raw = Array.isArray(data)
    ? data
    : (data && (data.RapidOrderLists || data.Lists)) || [];
  return raw.map(normalizeList);
}

export class OrderListManager extends React.Component {
  constructor(props) {
    super(props);
    const hasInitial = Array.isArray(props.initialLists);
    this.state = {
      lists: hasInitial ? props.initialLists.map(normalizeList) : [],
      loading: !hasInitial,
      editingName: "",
      originalName: null, // set when editing
      draftOrders: [], // {ID, Name}
      term: "",
      results: [],
    };
    this.search = null; // created in componentDidMount from props.api
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    this.search = this.props.api.createOrderSearch();

    // Always reconcile with the server on open so saved lists (which may differ
    // from the case-start snapshot) are accurate for editing.
    this.props.api
      .getRapidOrderLists()
      .then((data) => {
        if (!this._mounted) return;
        this.setState({ lists: normalizeListsPayload(data), loading: false });
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.search) this.search.cancel();
  }

  // The manager renders a normalized shape internally: { Name, orders:[{ID,Name}] }.
  // But the rest of the app (chips, initialLists) expects the API shape:
  // { Name, Orders:[{Id,Name}] }. Convert back before handing lists to onChange.
  toApiShape(normalized) {
    return normalized.map(function (l) {
      return {
        Name: l.Name,
        Orders: l.orders.map(function (o) {
          return { Id: o.ID, Name: o.Name };
        }),
      };
    });
  }

  refreshFromServer = () => {
    return this.props.api.getRapidOrderLists().then((data) => {
      const next = normalizeListsPayload(data);
      if (this._mounted) this.setState({ lists: next });
      if (this.props.onChange) this.props.onChange(this.toApiShape(next));
      return next;
    });
  };

  onTerm = (v) => {
    this.setState({ term: v });
    this.search(
      v,
      (list) => {
        this.setState({ results: list });
      },
      () => {
        this.setState({ results: [] });
      },
    );
  };

  addOrder = (o) => {
    const draftOrders = this.state.draftOrders;
    if (draftOrders.length >= MAX_ORDERS_PER_LIST) return;
    if (
      draftOrders.some(function (x) {
        return x.ID === o.ID;
      })
    )
      return;
    // From autocomplete, the display name is PrimaryOrder.
    this.setState({
      draftOrders: draftOrders.concat([{ ID: o.ID, Name: o.PrimaryOrder }]),
      term: "",
      results: [],
    });
  };

  removeOrder = (id) => {
    this.setState(function (prevState) {
      return {
        draftOrders: prevState.draftOrders.filter(function (x) {
          return x.ID !== id;
        }),
      };
    });
  };

  startEdit = (list) => {
    this.setState({
      originalName: list.Name,
      editingName: list.Name,
      draftOrders: list.orders.map(function (o) {
        return { ID: o.ID, Name: o.Name };
      }),
      term: "",
      results: [],
    });
  };

  resetDraft = () => {
    this.setState({
      originalName: null,
      editingName: "",
      draftOrders: [],
      term: "",
      results: [],
    });
  };

  saveDraft = () => {
    const { editingName, draftOrders, originalName } = this.state;
    const name = editingName.trim();
    if (!name || draftOrders.length === 0) return;
    const orderIds = draftOrders.map(function (x) {
      return x.ID;
    });
    const api = this.props.api;

    // If editing and the name changed, delete the old one first so we don't
    // leave a stale duplicate behind.
    const pre =
      originalName && originalName !== name
        ? api.deleteRapidOrderList(originalName)
        : Promise.resolve();

    pre
      .then(function () {
        return api.saveRapidOrderList(name, orderIds);
      })
      .then(() => {
        return this.refreshFromServer();
      })
      .then(() => {
        if (this._mounted) this.resetDraft();
      })
      .catch(function () {
        alert("Could not save list.");
      });
  };

  removeList = (name) => {
    this.props.api
      .deleteRapidOrderList(name)
      .then(() => {
        return this.refreshFromServer();
      })
      .then(() => {
        if (this._mounted && this.state.originalName === name)
          this.resetDraft();
      })
      .catch(function () {
        alert("Could not delete list.");
      });
  };

  render() {
    const {
      lists,
      loading,
      editingName,
      originalName,
      draftOrders,
      term,
      results,
    } = this.state;

    // When editing an existing list, that name doesn't count against the limit.
    const editingExisting = originalName != null;
    const atListLimit = !editingExisting && lists.length >= MAX_LISTS;

    return (
      <Modal
        title="Rapid order lists"
        onClose={this.props.onClose}
        footer={
          <button className="rccs-btn" onClick={this.props.onClose}>
            Done
          </button>
        }
      >
        {loading ? (
          <div className="rccs-loading">
            <div className="rccs-spinner" />
            Loading…
          </div>
        ) : (
          <React.Fragment>
            {lists.map((l) => {
              return (
                <div className="rccs-list-mgr__item" key={l.Name}>
                  <div className="rccs-list-mgr__head">
                    <span className="rccs-list-mgr__name">{l.Name}</span>
                    <span style={{ display: "flex", gap: 6 }}>
                      <button
                        className="rccs-btn rccs-btn--sm"
                        onClick={() => {
                          this.startEdit(l);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="rccs-btn rccs-btn--sm rccs-btn--danger"
                        onClick={() => {
                          this.removeList(l.Name);
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </div>
                  <div className="rccs-list-mgr__orders">
                    {l.orders.length === 0 ? (
                      <span className="rccs-list-mgr__order">No orders</span>
                    ) : (
                      l.orders.map(function (o, i) {
                        return (
                          <span
                            className="rccs-list-mgr__order"
                            key={o.ID + "-" + i}
                          >
                            {o.Name}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}

            {atListLimit ? (
              <p style={{ color: "var(--ink-faint)", fontSize: 13 }}>
                Maximum of {MAX_LISTS} lists reached. Delete or edit one to make
                changes.
              </p>
            ) : (
              <div
                style={{
                  marginTop: 16,
                  borderTop: "1px solid var(--line)",
                  paddingTop: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <strong>{editingExisting ? "Edit list" : "New list"}</strong>
                  {editingExisting && (
                    <button
                      className="rccs-btn rccs-btn--sm"
                      onClick={this.resetDraft}
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
                <div className="rccs-field">
                  <label>List name</label>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => {
                      this.setState({ editingName: e.target.value });
                    }}
                    placeholder="e.g. Chest Pain List"
                  />
                </div>
                <div className="rccs-field">
                  <label>
                    Add orders ({draftOrders.length}/{MAX_ORDERS_PER_LIST})
                  </label>
                  <div className="rccs-order-search">
                    <input
                      type="text"
                      value={term}
                      onChange={(e) => {
                        this.onTerm(e.target.value);
                      }}
                      placeholder="Type 3+ characters…"
                      disabled={draftOrders.length >= MAX_ORDERS_PER_LIST}
                    />
                    {results.length > 0 && (
                      <div className="rccs-ac">
                        {results.map((o) => {
                          return (
                            <div
                              key={o.ID}
                              className="rccs-ac__item"
                              onClick={() => {
                                this.addOrder(o);
                              }}
                            >
                              {/* suggestions show the matched term only */}
                              <div className="rccs-ac__matched-main">
                                {o.MatchedTerm}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {draftOrders.length > 0 && (
                  <div className="rccs-queue" style={{ marginBottom: 12 }}>
                    {draftOrders.map((o) => {
                      return (
                        <div className="rccs-queue__item" key={o.ID}>
                          <span className="rccs-queue__name">{o.Name}</span>
                          <button
                            className="rccs-queue__x"
                            onClick={() => {
                              this.removeOrder(o.ID);
                            }}
                            aria-label="Remove order"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  className="rccs-btn rccs-btn--primary"
                  onClick={this.saveDraft}
                  disabled={!editingName.trim() || draftOrders.length === 0}
                >
                  {editingExisting ? "Save changes" : "Save list"}
                </button>
              </div>
            )}
          </React.Fragment>
        )}
      </Modal>
    );
  }
}

// --------------------------------------------------------------------------
// Previous attempts modal
//
// Opened from the case selector when the user clicks a case that already has
// attempts (listrapidcases returns them on each row as caseRow.Attempts).
// The user can either start a fresh attempt or open the stored grade of a
// previous one. Fetching the grade itself (rapidccsgrading.webapi) lives in
// RapidCcsApp — this component is purely presentational.
//
// Props:
//   caseRow           the case row from listrapidcases            (required)
//   onClose           () => void                                  (required)
//   onStartNew        () => void — begin a fresh attempt          (required)
//   onViewAttempt     (attempt) => void — open that grade         (required)
//   loadingAttemptId  attempt ID currently being fetched | null   (optional)
//   error             string | null — fetch failure message       (optional)
// --------------------------------------------------------------------------

function formatAttemptDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export class PreviousAttemptsModal extends React.Component {
  render() {
    const {
      caseRow,
      onClose,
      onStartNew,
      onViewAttempt,
      loadingAttemptId,
      error,
    } = this.props;

    const attempts = Array.isArray(caseRow.Attempts)
      ? caseRow.Attempts.slice()
      : [];

    // Newest first — the most recent attempt is usually the one being reviewed.
    attempts.sort(function (a, b) {
      const da = new Date(a.CompletionDate).getTime() || 0;
      const db = new Date(b.CompletionDate).getTime() || 0;
      return db - da;
    });

    // Highest score gets a "Best" tag so it's easy to spot in long lists.
    let bestId = null;
    let bestGrade = -Infinity;
    attempts.forEach(function (a) {
      if (typeof a.GradeForCase === "number" && a.GradeForCase > bestGrade) {
        bestGrade = a.GradeForCase;
        bestId = a.ID;
      }
    });

    const anyLoading = loadingAttemptId != null;

    return (
      <Modal
        title={"Case #" + caseRow.ID + " — " + (caseRow.TITLE || "Case")}
        titleHidden={this.props.titleHidden}
        onClose={anyLoading ? function () {} : onClose}
        footer={
          <React.Fragment>
            <button
              className="rccs-btn"
              onClick={onClose}
              disabled={anyLoading}
            >
              Cancel
            </button>
            <button
              className="rccs-btn"
              onClick={onStartNew}
              disabled={anyLoading}
            >
              Start new attempt
            </button>
          </React.Fragment>
        }
      >
        <div className="rccs-attempts-summary">
          <div className="rccs-attempts-summary__count">
            <span className="rccs-attempts-summary__num">
              {attempts.length}
            </span>
            <span className="rccs-attempts-summary__label">
              {attempts.length === 1 ? "Attempt" : "Attempts"}
            </span>
          </div>
          <p className="rccs-attempts-summary__text">
            Start a new attempt, or review the grade from a previous one.
          </p>
        </div>

        {error && <div className="rccs-attempts__error">{error}</div>}

        <div className="rccs-attempts">
          {attempts.map((a, i) => {
            const isLoading = loadingAttemptId === a.ID;
            const isBest = a.ID === bestId && attempts.length > 1;
            const hasGrade = typeof a.GradeForCase === "number";
            return (
              <div
                className={
                  "rccs-attempts__row" +
                  (isLoading ? " rccs-attempts__row--loading" : "")
                }
                key={a.ID}
              >
                <div
                  className={
                    "rccs-attempts__num" +
                    (isBest ? " rccs-attempts__num--best" : "")
                  }
                >
                  {isBest ? "★" : "#" + (attempts.length - i)}
                </div>
                <div className="rccs-attempts__meta">
                  <div className="rccs-attempts__date">
                    {formatAttemptDate(a.CompletionDate)}
                    {isBest && (
                      <span className="rccs-attempts__best">Best</span>
                    )}
                  </div>
                  <div className="rccs-attempts__grade">
                    <span className="rccs-attempts__grade-label">Grade</span>
                    <span
                      className={
                        "rccs-attempts__grade-value" +
                        (hasGrade ? "" : " rccs-attempts__grade-value--none")
                      }
                    >
                      {hasGrade ? a.GradeForCase.toFixed(2) : "—"}
                    </span>
                  </div>
                </div>
                <button
                  className="rccs-btn rccs-btn--sm rccs-btn--outline"
                  onClick={() => {
                    onViewAttempt(a);
                  }}
                  disabled={anyLoading}
                >
                  {isLoading ? (
                    <React.Fragment>
                      <span className="rccs-attempts__spinner" />
                      Loading…
                    </React.Fragment>
                  ) : (
                    "View grade"
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </Modal>
    );
  }
}
